import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { JSX, ReactNode } from 'react';

import { useSesion } from '../piezas/Sesion.jsx';
import { api } from './api.js';

/**
 * Los tres filtros globales: cliente, ciudad y proyecto.
 *
 * Viven arriba de las rutas y no dentro de una pantalla, porque son del
 * encabezado: se eligen una vez y mandan sobre lo que se mire despues. Una
 * pantalla que los guardara por su cuenta los perderia al navegar, que es
 * justo lo contrario de para que sirven.
 */

export interface Filtros {
  cliente: string;
  ciudad: string;
  proyecto: string;
}

interface Opcion {
  id: string;
  nombre: string;
  /** Solo en proyectos: a que cliente pertenece. */
  cliente_id?: string | null;
}

interface Contexto {
  filtros: Filtros;
  /** Cambia uno. Tambien lo usan las graficas, para filtrar desde un punto. */
  poner: (cual: keyof Filtros, valor: string) => void;
  limpiar: () => void;
  /** `?cliente_id=...&ciudad_id=...`, vacio si no hay ninguno. */
  consulta: string;
  /** Si hay al menos uno puesto. */
  hayAlguno: boolean;
  clientes: Opcion[];
  ciudades: Opcion[];
  /** Ya acotados al cliente elegido. */
  proyectos: Opcion[];
  /** El perfil cliente no elige cliente: esta encerrado en el suyo. */
  clienteFijo: boolean;
}

const Ctx = createContext<Contexto | null>(null);

/** Una ciudad del filtro: con sus clientes, para poder acotarla. */
interface CiudadFiltro {
  id: string;
  clave: string;
  nombre: string;
  clientes: string[];
}

const VACIOS: Filtros = { cliente: '', ciudad: '', proyecto: '' };

export function ProveedorFiltros({ children }: { children: ReactNode }): JSX.Element {
  const { usuario } = useSesion();
  const [filtros, setFiltros] = useState<Filtros>(VACIOS);
  const [clientes, setClientes] = useState<Opcion[]>([]);
  const [todasCiudades, setTodasCiudades] = useState<CiudadFiltro[]>([]);
  const [todosProyectos, setTodosProyectos] = useState<Opcion[]>([]);

  /* El perfil cliente queda encerrado en el suyo. No es solo la interfaz: la
     RLS ya lo encierra del lado de la base, asi que esto evita ofrecerle un
     control que no haria nada, no es la proteccion. */
  const clienteFijo = usuario?.perfil === 'PERFIL_CLIENTE';

  useEffect(() => {
    if (!usuario) return;
    /* Las listas se piden una vez por sesion. Si una falla, su selector sale
       vacio y los otros dos siguen sirviendo: un filtro de menos es peor que
       una pantalla sin filtros, pero mucho mejor que una pantalla rota. */
    void api
      .obtener<Opcion[]>('/v1/clientes')
      .then(({ datos }) => setClientes(datos))
      .catch(() => setClientes([]));
    /* Las ciudades del FILTRO, no el catalogo.
    
       Antes se pedia `/v1/ciudades`, que es el catalogo entero: 164 ciudades
       en la base de demostracion, de las que 84 tienen algun sitio y solo 7
       pertenecen a Grupo Alsea. Quien trabaja esa cuenta tenia que encontrar
       sus 7 entre 164, y 80 de las 164 no pueden devolver nada en ninguna
       pantalla porque no tienen un solo sitio. Un filtro donde la mayoria de
       las opciones deja la pantalla en blanco no se siente como un filtro,
       se siente como una falla.
    
       El catalogo sigue siendo el correcto para CAPTURAR: al dar de alta un
       sitio hay que poder elegir una ciudad donde todavia no hay ninguno. */
    void api
      .obtener<CiudadFiltro[]>('/v1/filtros/ciudades')
      .then(({ datos }) => setTodasCiudades(datos))
      .catch(() => setTodasCiudades([]));
    void api
      .obtener<Opcion[]>('/v1/proyectos')
      .then(({ datos }) => setTodosProyectos(datos))
      .catch(() => setTodosProyectos([]));
  }, [usuario]);

  /* Al perfil cliente se le pone su propio cliente, para que el resto del
     codigo no tenga que preguntar por el perfil en cada consulta. */
  useEffect(() => {
    if (clienteFijo && usuario?.cliente_id) {
      setFiltros((f) =>
        f.cliente === usuario.cliente_id ? f : { ...f, cliente: usuario.cliente_id ?? '' },
      );
    }
  }, [clienteFijo, usuario]);

  const proyectos = useMemo(
    () => todosProyectos.filter((p) => !filtros.cliente || p.cliente_id === filtros.cliente),
    [todosProyectos, filtros.cliente],
  );

  /* Las ciudades se acotan al cliente elegido, igual que los proyectos. Para
     Grupo Alsea son 7 en vez de 84. Una ciudad puede tener sitios de varios
     clientes, asi que la pertenencia es una lista y no un campo. */
  const ciudades = useMemo<Opcion[]>(
    () =>
      todasCiudades
        .filter((c) => !filtros.cliente || c.clientes.includes(filtros.cliente))
        .map((c) => ({ id: c.id, nombre: c.nombre })),
    [todasCiudades, filtros.cliente],
  );

  const poner = useCallback(
    (cual: keyof Filtros, valor: string) => {
      setFiltros((f) => {
        const siguiente = { ...f, [cual]: valor };
        /* Cambiar de cliente puede dejar elegido un proyecto que ya no es
           suyo. Se limpia aqui y no se deja que el servidor devuelva vacio:
           una pantalla sin datos por una combinacion imposible parece una
           falla, y la persona no tiene como saber que el proyecto quedo
           colgando de otro cliente. */
        if (cual === 'cliente' && siguiente.proyecto) {
          const p = todosProyectos.find((x) => x.id === siguiente.proyecto);
          if (!p || (valor && p.cliente_id !== valor)) siguiente.proyecto = '';
        }
        /* Lo mismo con la ciudad, por la misma razon y con mas motivo: antes
           la ciudad no se limpiaba al cambiar de cliente, asi que quedaba
           elegida una ciudad donde el cliente nuevo no tiene un solo sitio y
           TODAS las pantallas salian vacias. Se veia como una falla de la
           plataforma y lo era: una combinacion imposible que la interfaz
           dejaba armar. */
        if (cual === 'cliente' && siguiente.ciudad) {
          const c = todasCiudades.find((x) => x.id === siguiente.ciudad);
          if (!c || (valor && !c.clientes.includes(valor))) siguiente.ciudad = '';
        }
        return siguiente;
      });
    },
    [todosProyectos, todasCiudades],
  );

  const limpiar = useCallback(() => {
    setFiltros(
      clienteFijo && usuario?.cliente_id ? { ...VACIOS, cliente: usuario.cliente_id } : VACIOS,
    );
  }, [clienteFijo, usuario]);

  const consulta = useMemo(() => {
    const p = new URLSearchParams();
    if (filtros.cliente) p.set('cliente_id', filtros.cliente);
    if (filtros.ciudad) p.set('ciudad_id', filtros.ciudad);
    if (filtros.proyecto) p.set('proyecto_id', filtros.proyecto);
    const s = p.toString();
    return s ? `?${s}` : '';
  }, [filtros]);

  /* Para el perfil cliente, su propio cliente no cuenta como filtro puesto:
     es su alcance, no una eleccion que pueda deshacer. */
  const hayAlguno =
    Boolean(filtros.ciudad) ||
    Boolean(filtros.proyecto) ||
    (!clienteFijo && Boolean(filtros.cliente));

  const valor = useMemo(
    () => ({
      filtros,
      poner,
      limpiar,
      consulta,
      hayAlguno,
      clientes,
      ciudades,
      proyectos,
      clienteFijo,
    }),
    [filtros, poner, limpiar, consulta, hayAlguno, clientes, ciudades, proyectos, clienteFijo],
  );

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useFiltros(): Contexto {
  const c = useContext(Ctx);
  if (!c) throw new Error('useFiltros fuera de ProveedorFiltros');
  return c;
}
